import { inject } from '@angular/core';
import {
  AbstractUseCaseCollection,
  Result,
  ResultStream,
  withUseCaseInteractor,
} from '@causality/core';
import { ConnectionService, hasConnectionStatus } from '@fmnts/common';
import { SelfCheckout } from '@formunauts/self-checkout/domain';
import { Data } from 'effect';
import { exhaustMap, first, pipe } from 'rxjs';
import { SelfCheckoutRepository } from './self-checkout-repository';

export class SelfCheckoutUseCaseConfig extends Data.Class<{
  timeout: number;
}> {}

export class SelfCheckoutUseCases extends AbstractUseCaseCollection {
  private readonly connection = inject(ConnectionService);
  private readonly resultStreamTimeoutConfig: ResultStream.TimeoutConfig = {
    each: inject(SelfCheckoutUseCaseConfig).timeout,
  };
  constructor(private readonly repo: SelfCheckoutRepository) {
    super();
  }

  readonly getAuthenticationForm = this.createUseCase(
    'self-checkout.getAuthenticationForm',
    withUseCaseInteractor<
      SelfCheckout.Hash,
      Result.Result<
        SelfCheckout.AuthenticationForm,
        SelfCheckout.SelfCheckoutError
      >
    >(() =>
      pipe(
        exhaustMap((hash) =>
          this.repo
            .getAuthenticationForm(hash)
            .pipe(
              ResultStream.failWhenOffline(this._isOnline$()),
              ResultStream.timeout(this.resultStreamTimeoutConfig),
            ),
        ),
      ),
    ),
  );

  readonly authenticate = this.createUseCase(
    'self-checkout.authenticate',
    withUseCaseInteractor<
      SelfCheckout.AuthenticationData,
      Result.Result<
        SelfCheckout.SelfCheckoutForm,
        SelfCheckout.SelfCheckoutError
      >
    >(() =>
      pipe(
        exhaustMap((verificationPayload) =>
          this.repo
            .authenticate(verificationPayload)
            .pipe(
              ResultStream.failWhenOffline(this._isOnline$()),
              ResultStream.timeout(this.resultStreamTimeoutConfig),
            ),
        ),
      ),
    ),
  );

  readonly checkoutTamaro = this.createUseCase(
    'self-checkout.checkoutTamaro',
    withUseCaseInteractor<
      SelfCheckout.PreparedSelfCheckoutFormPayload,
      Result.Result<
        SelfCheckout.SelfCheckoutResponseTamaro,
        SelfCheckout.SelfCheckoutError
      >
    >(() =>
      pipe(
        exhaustMap((preparedSelfCheckoutPayload) =>
          this.repo
            .checkoutTamaro(preparedSelfCheckoutPayload)
            .pipe(
              ResultStream.failWhenOffline(this._isOnline$()),
              ResultStream.timeout(this.resultStreamTimeoutConfig),
            ),
        ),
      ),
    ),
  );
  readonly checkoutDefault = this.createUseCase(
    'self-checkout.checkoutDefault',
    withUseCaseInteractor<
      SelfCheckout.PreparedSelfCheckoutFormPayload,
      Result.Result<
        SelfCheckout.SelfCheckoutResponseDefault,
        SelfCheckout.SelfCheckoutError
      >
    >(() =>
      pipe(
        exhaustMap((preparedSelfCheckoutPayload) =>
          this.repo
            .checkoutDefault(preparedSelfCheckoutPayload)
            .pipe(
              ResultStream.failWhenOffline(this._isOnline$()),
              ResultStream.timeout(this.resultStreamTimeoutConfig),
            ),
        ),
      ),
    ),
  );

  readonly getUnsubscribeForm = this.createUseCase(
    'self-checkout.getUnsubscribeForm',
    withUseCaseInteractor<
      SelfCheckout.Hash,
      Result.Result<
        SelfCheckout.UnsubscribeForm,
        SelfCheckout.SelfCheckoutError
      >
    >(() =>
      pipe(
        exhaustMap((hash) =>
          this.repo
            .getUnsubscribeForm(hash)
            .pipe(
              ResultStream.failWhenOffline(this._isOnline$()),
              ResultStream.timeout(this.resultStreamTimeoutConfig),
            ),
        ),
      ),
    ),
  );

  readonly unsubscribe = this.createUseCase(
    'self-checkout.unsubscribe',
    withUseCaseInteractor<
      SelfCheckout.PreparedUnsubscribeFormPayload,
      Result.Result<unknown, SelfCheckout.SelfCheckoutError>
    >(() =>
      pipe(
        exhaustMap((preparedData) =>
          this.repo
            .unsubscribe(preparedData)
            .pipe(
              ResultStream.failWhenOffline(this._isOnline$()),
              ResultStream.timeout(this.resultStreamTimeoutConfig),
            ),
        ),
      ),
    ),
  );

  private _isOnline$() {
    return this.connection.connectionStatus$.pipe(
      hasConnectionStatus('online'),
      first(),
    );
  }
}
